"use client";

import { c as _c } from "react/compiler-runtime";
import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useId } from "react";
import { ChatBubbleLeftRightIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ApiWave } from "@/generated/models/ApiWave";
import { getRandomColorWithSeed, numberWithCommas } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import WaveItemDropped from "./WaveItemDropped";
import WaveItemFollow from "./WaveItemFollow";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { Tooltip } from "react-tooltip";
const LEVEL_CLASSES: ReadonlyArray<{
  readonly minLevel: number;
  readonly classes: string;
}> = [{
  minLevel: 80,
  classes: "tw-text-[#55B075] tw-ring-[#55B075]"
}, {
  minLevel: 60,
  classes: "tw-text-[#AABE68] tw-ring-[#AABE68]"
}, {
  minLevel: 40,
  classes: "tw-text-[#DAC660] tw-ring-[#DAC660]"
}, {
  minLevel: 20,
  classes: "tw-text-[#DAAC60] tw-ring-[#DAAC60]"
}, {
  minLevel: 0,
  classes: "tw-text-[#DA8C60] tw-ring-[#DA8C60]"
}];
const DEFAULT_LEVEL_CLASS = LEVEL_CLASSES.at(-1)?.classes ?? "";
const CARD_BASE_CLASSES = "tw-@container/wave tw-group tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-backdrop-blur-sm tw-p-2.5 tw-shadow-sm tw-shadow-black/20 tw-transition-all tw-duration-300 tw-ease-out";
const CARD_INTERACTIVE_CLASSES = "tw-cursor-pointer desktop-hover:hover:tw-shadow-lg desktop-hover:hover:tw-shadow-black/40 desktop-hover:hover:tw-translate-y-[-1px] focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-900 focus-visible:tw-outline-none";
const INTERACTIVE_TAGS = new Set(["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA", "LABEL"]);
const shouldSkipNavigation = (target: HTMLElement | null, root: HTMLElement): boolean => {
  let current: HTMLElement | null = target;
  while (current) {
    if (current === root) {
      break;
    }
    if (current.dataset?.waveItemInteractive === "true") {
      return true;
    }
    if (INTERACTIVE_TAGS.has(current.tagName)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};
type CardContainerProps = {
  readonly isInteractive: boolean;
  readonly href?: string;
  readonly ariaLabel?: string;
  readonly children: ReactNode;
  readonly onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement>) => void;
};
function CardContainer(t0) {
  const $ = _c(11);
  const {
    isInteractive,
    href,
    ariaLabel,
    onClick,
    onKeyDown,
    children
  } = t0;
  const className = `${CARD_BASE_CLASSES} ${isInteractive ? CARD_INTERACTIVE_CLASSES : ""} tw-no-underline`;
  if (isInteractive && href) {
    let t1;
    if ($[0] !== ariaLabel || $[1] !== children || $[2] !== className || $[3] !== href || $[4] !== onClick || $[5] !== onKeyDown) {
      t1 = <Link href={href} prefetch={false} className={className} aria-label={ariaLabel} onClick={onClick} onKeyDown={onKeyDown}>{children}</Link>;
      $[0] = ariaLabel;
      $[1] = children;
      $[2] = className;
      $[3] = href;
      $[4] = onClick;
      $[5] = onKeyDown;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== ariaLabel || $[8] !== children || $[9] !== className) {
    t1 = <div className={className} aria-label={ariaLabel}>{children}</div>;
    $[7] = ariaLabel;
    $[8] = children;
    $[9] = className;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function getCardLabel(href?: string, label?: string | null) {
  if (!href) {
    return undefined;
  }
  return label ? `View wave ${label}` : "View wave";
}
function resolveLevelClasses(level?: number | null) {
  return LEVEL_CLASSES.find(levelClass => levelClass.minLevel <= (level ?? 0))?.classes ?? DEFAULT_LEVEL_CLASS;
}
export default function WaveItem(t0) {
  const $ = _c(109);
  const {
    wave,
    userPlaceholder,
    titlePlaceholder
  } = t0;
  const router = useRouter();
  const author = wave?.author;
  const authorHref = author?.handle ? `/${author.handle}` : undefined;
  const authorLevel = author?.level ?? 0;
  const tooltipBaseId = useId();
  let t1;
  if ($[0] !== author?.banner1_color || $[1] !== author?.handle || $[2] !== userPlaceholder) {
    t1 = author?.banner1_color ?? getRandomColorWithSeed(author?.handle ?? userPlaceholder ?? "");
    $[0] = author?.banner1_color;
    $[1] = author?.handle;
    $[2] = userPlaceholder;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const banner1 = t1;
  let t2;
  if ($[4] !== author?.banner2_color || $[5] !== author?.handle || $[6] !== userPlaceholder) {
    t2 = author?.banner2_color ?? getRandomColorWithSeed(author?.handle ?? userPlaceholder ?? "");
    $[4] = author?.banner2_color;
    $[5] = author?.handle;
    $[6] = userPlaceholder;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const banner2 = t2;
  const isDirectMessage = wave?.chat?.scope?.group?.is_direct_message ?? false;
  let t3;
  let waveHref;
  if ($[8] !== isDirectMessage || $[9] !== wave) {
    waveHref = wave ? getWaveRoute({
      waveId: wave.id,
      isDirectMessage,
      isApp: false
    }) : undefined;
    const labelValue = wave?.name ?? wave?.id;
    t3 = getCardLabel(waveHref, labelValue);
    $[8] = isDirectMessage;
    $[9] = wave;
    $[10] = t3;
    $[11] = waveHref;
  } else {
    t3 = $[10];
    waveHref = $[11];
  }
  const cardLabel = t3;
  const isInteractive = Boolean(waveHref);
  const followersTooltipId = wave ? `${tooltipBaseId}-followers` : undefined;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <UserGroupIcon aria-hidden="true" className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-400" />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = wave?.metrics.subscribers_count ?? 0;
  let t6;
  if ($[13] !== t5) {
    t6 = numberWithCommas(t5);
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <span className="tw-font-medium">{t6}</span>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="tw-text-iron-400 xl:tw-hidden">Joined</span>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7) {
    t9 = <>{t4}{t7}{t8}</>;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const followersContent = t9;
  let t10;
  if ($[20] !== author) {
    t10 = author?.pfp ? <img className="tw-h-full tw-w-full tw-rounded-md tw-object-cover tw-bg-iron-800 tw-ring-1 tw-ring-white/10 desktop-hover:group-hover/author:tw-ring-white/30 desktop-hover:group-hover/author:tw-ring-offset-1 desktop-hover:group-hover/author:tw-ring-offset-iron-950 tw-transition tw-duration-300 tw-ease-out" src={getScaledImageUri(author.pfp, ImageScale.W_AUTO_H_50)} alt={author?.handle ? `${author.handle} avatar` : "Author avatar"} loading="lazy" decoding="async" /> : <div className="tw-h-full tw-w-full tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10" />;
    $[20] = author;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const authorAvatar = t10;
  const t11 = author?.level;
  let t12;
  if ($[22] !== t11) {
    t12 = resolveLevelClasses(t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = `${t12} tw-border-none tw-inline-flex tw-items-center tw-rounded-xl tw-bg-transparent tw-px-2 tw-py-1 tw-font-semibold tw-ring-2 tw-ring-inset tw-text-[0.625rem] tw-leading-3`;
  let t14;
  if ($[24] !== authorLevel || $[25] !== t13) {
    t14 = <div className={t13}>Level {authorLevel}</div>;
    $[24] = authorLevel;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const authorLevelBadge = t14;
  let t15;
  if ($[27] !== authorHref || $[28] !== router) {
    t15 = event => {
      if (!authorHref) {
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
        window.open(authorHref, "_blank", "noopener,noreferrer");
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      router.push(authorHref);
    };
    $[27] = authorHref;
    $[28] = router;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const handleAuthorClick = t15;
  let t16;
  if ($[30] !== authorHref) {
    t16 = event_0 => {
      if (!authorHref || event_0.button !== 1) {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      window.open(authorHref, "_blank", "noopener,noreferrer");
    };
    $[30] = authorHref;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const handleAuthorAuxClick = t16;
  let authorSection;
  if (!wave) {
    let t17;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-iron-700" />;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== userPlaceholder) {
      t18 = <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t17}<span className="tw-text-sm tw-font-semibold tw-text-white">{userPlaceholder}</span></span>;
      $[33] = userPlaceholder;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    authorSection = t18;
  } else {
    if (authorHref) {
      const t17 = author?.handle ? `View @${author.handle}` : "View author profile";
      let t18;
      if ($[35] !== authorAvatar) {
        t18 = <div className="tw-h-6 tw-w-6 tw-flex-shrink-0">{authorAvatar}</div>;
        $[35] = authorAvatar;
        $[36] = t18;
      } else {
        t18 = $[36];
      }
      const t19 = author?.handle ?? userPlaceholder;
      let t20;
      if ($[37] !== t19) {
        t20 = <span className="tw-text-sm tw-font-semibold tw-text-white desktop-hover:group-hover/author:tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out">{t19}</span>;
        $[37] = t19;
        $[38] = t20;
      } else {
        t20 = $[38];
      }
      let t21;
      if ($[39] !== authorLevelBadge || $[40] !== handleAuthorAuxClick || $[41] !== handleAuthorClick || $[42] !== t17 || $[43] !== t18 || $[44] !== t20) {
        t21 = <button type="button" data-wave-item-interactive="true" onClick={handleAuthorClick} onAuxClick={handleAuthorAuxClick} className="tw-mt-1 tw-group/author tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-cursor-pointer tw-no-underline tw-bg-transparent tw-border-none tw-p-0 tw-text-left" aria-label={t17}>{t18}{t20}{authorLevelBadge}</button>;
        $[39] = authorLevelBadge;
        $[40] = handleAuthorAuxClick;
        $[41] = handleAuthorClick;
        $[42] = t17;
        $[43] = t18;
        $[44] = t20;
        $[45] = t21;
      } else {
        t21 = $[45];
      }
      authorSection = t21;
    } else {
      let t17;
      if ($[46] !== authorAvatar) {
        t17 = <div className="tw-h-6 tw-w-6 tw-flex-shrink-0">{authorAvatar}</div>;
        $[46] = authorAvatar;
        $[47] = t17;
      } else {
        t17 = $[47];
      }
      const t18 = author?.handle ?? userPlaceholder;
      let t19;
      if ($[48] !== t18) {
        t19 = <span className="tw-text-sm tw-font-semibold tw-text-white">{t18}</span>;
        $[48] = t18;
        $[49] = t19;
      } else {
        t19 = $[49];
      }
      let t20;
      if ($[50] !== authorLevelBadge || $[51] !== t17 || $[52] !== t19) {
        t20 = <div className="tw-mt-1 tw-group/author tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t17}{t19}{authorLevelBadge}</div>;
        $[50] = authorLevelBadge;
        $[51] = t17;
        $[52] = t19;
        $[53] = t20;
      } else {
        t20 = $[53];
      }
      authorSection = t20;
    }
  }
  let t17;
  if ($[54] !== router || $[55] !== waveHref) {
    t17 = event_1 => {
      if (!waveHref) {
        return;
      }
      const target = event_1.target as HTMLElement | null;
      if (shouldSkipNavigation(target, event_1.currentTarget)) {
        event_1.preventDefault();
        event_1.stopPropagation();
        return;
      }
      if (event_1.button === 1 || event_1.metaKey || event_1.ctrlKey) {
        return;
      }
      if (!event_1.defaultPrevented) {
        event_1.preventDefault();
        router.push(waveHref);
      }
    };
    $[54] = router;
    $[55] = waveHref;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const handleCardClick = t17;
  let t18;
  if ($[57] !== router || $[58] !== waveHref) {
    t18 = event_2 => {
      if (!waveHref || event_2.target !== event_2.currentTarget) {
        return;
      }
      if (event_2.key === "Enter" || event_2.key === " " || event_2.key === "Space") {
        event_2.preventDefault();
        router.push(waveHref);
      }
    };
    $[57] = router;
    $[58] = waveHref;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  const handleCardKeyDown = t18;
  const t19 = `linear-gradient(45deg, ${banner1} 0%, ${banner2} 100%)`;
  const t20 = wave?.picture ? 0.35 : 1;
  let t21;
  if ($[60] !== t19 || $[61] !== t20) {
    t21 = <div className="tw-absolute tw-inset-0 tw-rounded-xl" style={{
      background: t19,
      opacity: t20
    }} />;
    $[60] = t19;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== wave) {
    t22 = wave?.picture && <img src={getScaledImageUri(wave.picture, ImageScale.AUTOx450)} alt={wave?.name ? `Wave ${wave.name}` : "Wave picture"} loading="lazy" decoding="async" className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-transition-transform tw-duration-500 tw-will-change-transform desktop-hover:group-hover:tw-scale-[1.015]" />;
    $[63] = wave;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-36 sm:tw-h-44 md:tw-h-48" style={{
      background: "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.64) 32%, rgba(0,0,0,0.30) 64%, rgba(0,0,0,0.00) 100%)"
    }} />;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  const t24 = wave?.name ?? titlePlaceholder;
  let t25;
  if ($[66] !== t24) {
    t25 = <div className="tw-flex tw-min-w-0 tw-items-end tw-px-3 tw-pb-3"><div className="tw-min-w-0"><span className="tw-text-lg tw-font-semibold tw-text-white desktop-hover:group-hover:tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out tw-line-clamp-1">{t24}</span></div></div>;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="tw-hidden sm:tw-block" />;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== t25) {
    t27 = <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex tw-items-end tw-justify-between tw-gap-3">{t25}{t26}</div>;
    $[69] = t25;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t21 || $[72] !== t22 || $[73] !== t27) {
    t28 = <div className="tw-relative tw-overflow-hidden tw-rounded-xl tw-aspect-[16/8.5] sm:tw-aspect-[16/9]">{t21}{t22}{t23}{t27}</div>;
    $[71] = t21;
    $[72] = t22;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== authorSection) {
    t29 = <div className="tw-px-3 tw-pt-3">{authorSection}</div>;
    $[75] = authorSection;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="tw-text-sm tw-flex tw-items-center tw-gap-x-2 tw-text-iron-200"><ChatBubbleLeftRightIcon aria-hidden="true" className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-400" /><span className="tw-font-medium">Chat</span></div>;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== followersContent || $[79] !== followersTooltipId || $[80] !== waveHref) {
    t31 = <div className="tw-flex tw-items-center tw-gap-4">{t30}{waveHref && followersTooltipId ? <span className="tw-text-sm tw-flex tw-items-center tw-gap-x-2 tw-text-iron-200 tw-no-underline tw-relative tw-z-20 tw-pointer-events-auto" data-tooltip-id={followersTooltipId}>{followersContent}</span> : <div className="tw-text-sm tw-flex tw-items-center tw-gap-x-2 tw-text-iron-200">{followersContent}</div>}</div>;
    $[78] = followersContent;
    $[79] = followersTooltipId;
    $[80] = waveHref;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== followersTooltipId || $[83] !== wave) {
    t32 = wave && followersTooltipId && <Tooltip id={followersTooltipId} place="top" positionStrategy="fixed" style={{
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    }}><span className="tw-text-xs">Joined</span></Tooltip>;
    $[82] = followersTooltipId;
    $[83] = wave;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  let t33;
  if ($[85] !== t31 || $[86] !== t32) {
    t33 = <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-px-3 tw-pt-3">{t31}{t32}</div>;
    $[85] = t31;
    $[86] = t32;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  let t34;
  if ($[88] !== wave) {
    t34 = wave && <div className="tw-overflow-hidden tw-min-w-0"><WaveItemDropped wave={wave} /></div>;
    $[88] = wave;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  let t35;
  if ($[90] !== t34) {
    t35 = <div className="tw-min-w-0 tw-flex-1">{t34}</div>;
    $[90] = t34;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] !== wave) {
    t36 = wave && <div data-wave-item-interactive="true" className="tw-relative tw-z-20 tw-pointer-events-auto"><WaveItemFollow wave={wave} /></div>;
    $[92] = wave;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== t36) {
    t37 = <div className="tw-flex tw-items-center">{t36}</div>;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t35 || $[97] !== t37) {
    t38 = <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-pb-1">{t35}{t37}</div>;
    $[96] = t35;
    $[97] = t37;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  let t39;
  if ($[99] !== cardLabel || $[100] !== handleCardClick || $[101] !== handleCardKeyDown || $[102] !== isInteractive || $[103] !== t28 || $[104] !== t29 || $[105] !== t33 || $[106] !== t38 || $[107] !== waveHref) {
    t39 = <CardContainer isInteractive={isInteractive} href={waveHref} ariaLabel={cardLabel} onClick={handleCardClick} onKeyDown={handleCardKeyDown}>{t28}{t29}{t33}{t38}</CardContainer>;
    $[99] = cardLabel;
    $[100] = handleCardClick;
    $[101] = handleCardKeyDown;
    $[102] = isInteractive;
    $[103] = t28;
    $[104] = t29;
    $[105] = t33;
    $[106] = t38;
    $[107] = waveHref;
    $[108] = t39;
  } else {
    t39 = $[108];
  }
  return t39;
}